'use server';

/**
 * Kontora BFF – AccessControl Server Actions
 */

import { cache } from 'react';
import { revalidatePath } from 'next/cache';
import { cookies } from 'next/headers';
import { callInternalApi } from '../api-client/internal-api-client';
import { logger } from '../../lib/logger';

const COOKIE_NAME = process.env['SESSION_COOKIE_NAME'] ?? 'kontora_session';
const FALLBACK_COOKIE_NAME = 'kontora_session';
const LOG_CONTEXT = 'access-control.actions';

function readToken(cookieStore: Awaited<ReturnType<typeof cookies>>): string | undefined {
  return cookieStore.get(COOKIE_NAME)?.value ?? cookieStore.get(FALLBACK_COOKIE_NAME)?.value;
}

function isUnauthenticatedError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const status = (error as { status?: unknown }).status;
  const code = (error as { code?: unknown }).code;
  return status === 401 || code === 'AUTH_REQUIRED' || code === 'SESSION_EXPIRED';
}

export const getMyPermissions = cache(async (): Promise<{ permissions: string[] } | null> => {
  try {
    const cookieStore = await cookies();
    const token = readToken(cookieStore);
    if (!token) return null;

    const response = await callInternalApi<{ permissions: string[] }>(
      'GET',
      '/access/me/permissions',
      { sessionToken: token },
    );

    return response.data ?? null;
  } catch (error) {
    if (!isUnauthenticatedError(error)) {
      logger.error({ error }, LOG_CONTEXT, 'BFF getMyPermissions error');
    }
    return null;
  }
});

export async function checkPermission(permission: string): Promise<boolean> {
  try {
    const cookieStore = await cookies();
    const token = readToken(cookieStore);
    if (!token) return false;

    const response = await callInternalApi<{ allowed: boolean }>('POST', '/access/check', {
      body: { permission },
      sessionToken: token,
    });

    return response.data?.allowed ?? false;
  } catch (error) {
    if (!isUnauthenticatedError(error)) {
      logger.error({ error }, LOG_CONTEXT, 'BFF checkPermission error');
    }
    return false;
  }
}

export interface RoleRow {
  public_id: string;
  name: string;
  description: string | null;
  is_system: boolean;
  permission_codes: string[];
  permission_count: number;
}

/**
 * CRIT-MOCK-07: Liefert die im aktiven Mandanten verfügbaren Rollen.
 * Ersetzt den direkten ROLE_SEEDS-Import in der BFF-Page.
 */
export async function getRoles(): Promise<RoleRow[] | { error: string }> {
  try {
    const cookieStore = await cookies();
    const token = cookieStore.get(COOKIE_NAME)?.value;
    if (!token) return { error: 'Nicht angemeldet.' };

    const response = await callInternalApi<RoleRow[]>('GET', '/access/roles', {
      sessionToken: token,
    });

    return response.data ?? [];
  } catch (error) {
    logger.error({ error }, LOG_CONTEXT, 'BFF getRoles error');
    return { error: 'Fehler beim Laden der Rollen.' };
  }
}

export interface MembershipRow {
  tenantId: string;
  tenantPublicId: string;
  tenantName: string;
  tenantStatus: string;
  membershipStatus: string;
  roleName: string;
  isActiveTenant: boolean;
}

/**
 * N-10: Liefert die aktiven Mandants-Memberships des Users.
 * Read-Only Workaround bis ein TenantsController verfuegbar ist.
 */
export async function getMyMemberships(): Promise<MembershipRow[] | { error: string }> {
  try {
    const cookieStore = await cookies();
    const token = cookieStore.get(COOKIE_NAME)?.value;
    if (!token) return { error: 'Nicht angemeldet.' };

    const response = await callInternalApi<{ memberships: MembershipRow[] }>(
      'GET',
      '/access/memberships',
      { sessionToken: token },
    );

    return response.data?.memberships ?? [];
  } catch (error) {
    logger.error({ error }, LOG_CONTEXT, 'BFF getMyMemberships error');
    return { error: 'Fehler beim Laden der Mandanten.' };
  }
}

export interface SwitchTenantResult {
  ok: boolean;
  error?: string;
}

export async function switchTenantAction(tenantPublicId: string): Promise<SwitchTenantResult> {
  const normalizedTenantId = tenantPublicId.trim();
  if (!normalizedTenantId) return { ok: false, error: 'Mandant-ID fehlt.' };

  try {
    const cookieStore = await cookies();
    const token = cookieStore.get(COOKIE_NAME)?.value;
    if (!token) return { ok: false, error: 'Nicht angemeldet.' };

    const response = await callInternalApi<{ activeTenantPublicId: string | null }>(
      'POST',
      '/auth/switch-tenant',
      {
        body: { tenantPublicId: normalizedTenantId },
        sessionToken: token,
      },
    );

    if (!response.success || response.data?.activeTenantPublicId !== normalizedTenantId) {
      return { ok: false, error: 'Mandant konnte nicht gewechselt werden.' };
    }

    revalidatePath('/', 'layout');
    return { ok: true };
  } catch (error) {
    logger.error({ error }, LOG_CONTEXT, 'BFF switchTenantAction error');
    return { ok: false, error: 'Mandant konnte nicht gewechselt werden.' };
  }
}
